import useFetchSubscription from "api/SubscriptionsResource/useFetchSubscription";
import { Container } from "layouts";
import PaddingX from "layouts/PaddingX";
import React from "react";
import { useParams } from "react-router-dom";
import useAuthStore from "store/authStore";
import useCurrencyStore from "store/currencyStore";
import EcocashPaymentZwl from "./EcocashPaymentZwl";
import { Link, useLocation } from "react-router-dom";
import EcocashPaymentUsd from "./EcocashPaymentUsd";
import PayPal from "./PayPal";
import StarsPay from "./StarsPay";
import VoucherPayment from "./VoucherPayment";
import useCheckIfUserIsSubscribed from "api/SubscriptionsResource/useCheckIfUserIsSubscribed";
import { activeCurrencies, activePaymentMethods, getMoneyPrefix } from "utils";
import useProfile from "api/UserResource/useProfile";
import { FaGlobe, FaPaypal, FaStar, FaTicketAlt, FaLock } from "react-icons/fa";
import { Role } from "utils/server-constants/roles";
import SubscriptionCheckoutPageLoader from "./SubscriptionCheckoutPageLoader";

const SubscriptionCheckout = () => {
	const isAuthenticated = useAuthStore((state) => state.loggedIn);
	const hasRoles = useAuthStore((state) => state.hasRoles);
	const { currency, paymentMethod, setPaymentMethod, setCurrency } = useCurrencyStore();
	const { subscriptionId } = useParams();

	const { data: subscription, isLoading } = useFetchSubscription(subscriptionId);
	const { data: profile, isLoading: isLoadingProfile } = useProfile();
	const { data: isSubscribed, isLoading: isLoadingSubscribed } = useCheckIfUserIsSubscribed();

	const hasActiveSubscription = Boolean(isSubscribed?.data);
	const location = useLocation();

	const id = subscription?.data?.id;
	const usdPrice = subscription?.data?.cost;
	const zwlPrice = subscription?.data?.zwl_price;
	const credits = subscription?.data?.credits;
	const category = subscription?.data?.category;
	const isInstitutional = category === "institution";

	const canAddSeats = isInstitutional && hasRoles([Role.Institution, Role.Educator]);

	// State for number of seats (for institutional subscriptions)
	const [numberOfSeats, setNumberOfSeats] = React.useState(1);

	// Toast notification state
	// const [showToast, setShowToast] = React.useState(false);
	// const [toastMessage, setToastMessage] = React.useState("");
	const [prevCurrency, setPrevCurrency] = React.useState(currency);
	const [showMobileOrderSummary, setShowMobileOrderSummary] = React.useState(false);

	const baseCost = React.useMemo(() => {
		return currency === activeCurrencies.USD.code ? usdPrice : zwlPrice;
	}, [currency, usdPrice, zwlPrice]);

	const cost = React.useMemo(() => {
		return isInstitutional ? baseCost * numberOfSeats : baseCost;
	}, [baseCost, numberOfSeats, isInstitutional]);

	const duration = React.useMemo(() => {
		// Use the actual duration and time_unit from subscription data
		if (subscription?.data?.duration && subscription?.data?.time_unit) {
			return `${subscription.data.duration} ${subscription.data.time_unit}`;
		}

		// Fallback to name-based logic for backward compatibility
		if (subscription?.data?.name.toLowerCase() === "weekly") {
			return "7 days";
		}
		if (subscription?.data?.name.toLowerCase() === "monthly") {
			return "30 days";
		}

		if (subscription?.data?.name.toLowerCase() === "yearly") {
			return "365 days";
		}

		if (subscription?.data?.name.toLowerCase() === "termly") {
			return "120 days";
		}

		return undefined;
	}, [subscription]);

	// Filter payment methods based on selected currency
	const paymentOptionsButtons = React.useMemo(() => {
		const allPaymentOptionsButtons = [
			{
				paymentMethod: activePaymentMethods.ecocashUsd.name,
				label: activePaymentMethods.ecocashUsd.label,
				icon: <FaGlobe size={22} className="text-green-600" />,
				supportedCurrencies: [activeCurrencies.USD.code],
				processingTime: "2-3 mins",
				description: "Pay with EcoCash USD wallet",
				iconBg: "bg-green-50",
				recommended: false,
			},
			{
				paymentMethod: activePaymentMethods.ecocashZwl.name,
				label: activePaymentMethods.ecocashZwl.label,
				icon: <FaGlobe size={22} className="text-green-600" />,
				supportedCurrencies: [activeCurrencies.ZWL.code],
				processingTime: "2-3 mins",
				description: "Pay with EcoCash ZWL wallet",
				iconBg: "bg-green-50",
				recommended: true,
			},
			{
				paymentMethod: activePaymentMethods.paypal.name,
				label: activePaymentMethods.paypal.label,
				icon: <FaPaypal size={22} className="text-akgreen" />,
				supportedCurrencies: [activeCurrencies.USD.code],
				processingTime: "Instant",
				description: "Secure payment via PayPal",
				iconBg: "bg-akgreen/10",
				recommended: true,
			},
			{
				paymentMethod: activePaymentMethods.stars.name,
				label: activePaymentMethods.stars.label,
				icon: <FaStar size={22} className="text-yellow-500" />,
				supportedCurrencies: [activeCurrencies.ZWL.code],
				processingTime: "Instant",
				description: "Use your Akello Smart Learning stars",
				iconBg: "bg-yellow-50",
				recommended: false,
			},
			{
				paymentMethod: activePaymentMethods.voucher.name,
				label: activePaymentMethods.voucher.label,
				icon: <FaTicketAlt size={22} className="text-purple-600" />,
				supportedCurrencies: [activeCurrencies.USD.code, activeCurrencies.ZWL.code], // Supports both
				processingTime: "Instant",
				description: "Redeem a prepaid voucher code",
				iconBg: "bg-purple-50",
				recommended: false,
			},
		];

		return allPaymentOptionsButtons.filter((option) =>
			option.supportedCurrencies.includes(currency),
		);
	}, [currency]);

	// Show toast notification when currency changes
	React.useEffect(() => {
		// if (prevCurrency && prevCurrency !== currency) {
		// const currencyLabel =
		// 	currency === activeCurrencies.USD.code
		// 		? activeCurrencies.USD.label
		// 		: activeCurrencies.ZWL.label;
		// setToastMessage(`Switched to ${currencyLabel} - showing compatible payment methods`);
		// setShowToast(true);
		// }
		setPrevCurrency(currency);
	}, [currency, prevCurrency]);

	// Auto-select first available payment method when currency changes
	React.useEffect(() => {
		const currentMethodSupported = paymentOptionsButtons.some(
			(option) => option.paymentMethod === paymentMethod,
		);

		if (!currentMethodSupported && paymentOptionsButtons.length > 0) {
			setPaymentMethod(paymentOptionsButtons[0].paymentMethod);
		}
	}, [currency, paymentOptionsButtons, paymentMethod, setPaymentMethod]);

	const renderPaymentContent = (method) => {
		if (method === activePaymentMethods.ecocashZwl.name && currency === activeCurrencies.ZWL.code) {
			return (
				<EcocashPaymentZwl
					grandTotal={cost}
					id={id}
					duration={duration}
					recurringSubscription={profile?.data?.recurred_subscription}
					numberOfSeats={isInstitutional ? numberOfSeats : undefined}
				/>
			);
		}

		if (method === activePaymentMethods.ecocashUsd.name && currency === activeCurrencies.USD.code) {
			return (
				<EcocashPaymentUsd
					grandTotal={cost}
					id={id}
					duration={duration}
					recurringSubscription={profile?.data?.recurred_subscription}
					numberOfSeats={isInstitutional ? numberOfSeats : undefined}
				/>
			);
		}

		if (method === activePaymentMethods.paypal.name && currency === activeCurrencies.USD.code) {
			return (
				<PayPal
					id={id}
					grandTotal={cost}
					numberOfSeats={isInstitutional ? numberOfSeats : undefined}
				/>
			);
		}

		if (method === activePaymentMethods.stars.name && currency === activeCurrencies.ZWL.code) {
			return (
				<StarsPay
					id={id}
					grandTotal={cost}
					credits={credits}
					numberOfSeats={isInstitutional ? numberOfSeats : undefined}
				/>
			);
		}

		if (method === activePaymentMethods.voucher.name) {
			return (
				<VoucherPayment
					grandTotal={cost}
					id={id}
					numberOfSeats={isInstitutional ? numberOfSeats : undefined}
				/>
			);
		}

		return (
			<div className="p-4 text-center text-gray-500">
				This payment method is not available with the selected currency.
			</div>
		);
	};

	if (isLoading || isLoadingSubscribed || isLoadingProfile) {
		return <SubscriptionCheckoutPageLoader />;
	}

	if (!isAuthenticated) {
		return (
			<Container>
				<PaddingX>
					<div className="min-h-[60vh] flex items-center justify-center">
						<div className="bg-white rounded-lg border border-gray-200 p-8 max-w-md w-full text-center">
							<h1 className="text-3xl font-bold text-gray-800 mb-6">Login Required</h1>
							<p className="text-gray-600 mb-8">Please log in to continue with your subscription</p>
							<Link
								to={`/login?intended=${location.pathname}`}
								className="inline-block bg-akgreen text-white font-semibold py-3 px-6 rounded-lg transition-all hover:bg-akgreener hover:shadow-md"
							>
								Log In
							</Link>
						</div>
					</div>
				</PaddingX>
			</Container>
		);
	}

	return (
		<Container>
			<div className="mt-4 md:mt-8 flex-1 pb-24 lg:pb-8">
				<PaddingX>
					<div className="max-w-3xl mx-auto">
						<div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
							{/* Main Payment Content */}
							<div className="lg:col-span-3">
								<div className="bg-white rounded-lg border border-gray-200 p-5">
									{hasActiveSubscription ? (
										<div className="text-center space-y-6">
											<div className="bg-amber-50/50 rounded-xl p-6">
												<h4 className="text-xl font-semibold text-gray-700 mb-4">
													Active Subscription Notice
												</h4>
												<p className="text-gray-700 mb-4">
													You already have an active subscription. Note that you can only have one
													active subscription at a time.
												</p>
												<p className="text-gray-700">
													You can add books to your picks and read them whilst you have an active
													subscription.
												</p>
											</div>
											<Link
												to="/books"
												className="inline-block bg-akgreen text-white font-semibold py-3 px-8 rounded-lg transition-all hover:bg-akgreener hover:shadow-md"
											>
												Browse Books
											</Link>
										</div>
									) : (
										<>
											<h2 className="text-base font-semibold text-gray-900 mb-4">Payment</h2>

											<div className="space-y-4">
												{/* Currency Selector */}
												<div className="mb-4 md:mb-6">
													<label className="block text-sm font-medium text-gray-700 mb-2">
														Currency
													</label>
													<div className="inline-flex rounded-full p-1 bg-gray-100">
														<button
															type="button"
															onClick={() => setCurrency(activeCurrencies.USD.code)}
															className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
																currency === activeCurrencies.USD.code
																	? "bg-akgreen text-white"
																	: "text-gray-700 hover:text-gray-900"
															}`}
														>
															{activeCurrencies.USD.label}
														</button>
														<button
															type="button"
															onClick={() => setCurrency(activeCurrencies.ZWL.code)}
															className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
																currency === activeCurrencies.ZWL.code
																	? "bg-akgreen text-white"
																	: "text-gray-700 hover:text-gray-900"
															}`}
														>
															{activeCurrencies.ZWL.label}
														</button>
													</div>
												</div>

												{paymentOptionsButtons.length > 0 ? (
													<div className="space-y-3">
														{/* Payment Method Pills */}
														<div>
															<p className="text-xs font-medium text-gray-600 mb-1.5">
																Payment method
															</p>
															<div className="flex flex-wrap gap-1.5">
																{paymentOptionsButtons.map((button) => {
																	const isSelected = paymentMethod === button.paymentMethod;
																	return (
																		<button
																			key={button.paymentMethod}
																			type="button"
																			onClick={() => setPaymentMethod(button.paymentMethod)}
																			className={`flex items-center gap-1.5 px-3 py-2 rounded-md border text-sm font-medium transition-colors ${
																				isSelected
																					? "border-akgreen bg-akgreen/5 text-akgreen"
																					: "border-gray-200 bg-white text-gray-600 hover:border-gray-300"
																			}`}
																		>
																			<span className="w-4 h-4 flex items-center justify-center [&>svg]:w-4 [&>svg]:h-4">
																				{button.icon}
																			</span>
																			<span>{button.label}</span>
																		</button>
																	);
																})}
															</div>
														</div>

														{/* Payment Form */}
														<div className="border border-gray-200 rounded-lg p-4 bg-gray-50/50">
															{(() => {
																const selectedOption = paymentOptionsButtons.find(
																	(o) =>
																		o.paymentMethod ===
																		(paymentMethod || paymentOptionsButtons[0]?.paymentMethod),
																);
																return (
																	<>
																		{selectedOption && (
																			<p className="text-xs text-gray-600 mb-3">
																				{selectedOption.description} •{" "}
																				{selectedOption.processingTime}
																			</p>
																		)}
																		{renderPaymentContent(
																			paymentMethod || paymentOptionsButtons[0]?.paymentMethod,
																		)}
																	</>
																);
															})()}
														</div>
													</div>
												) : (
													<div className="text-center py-6 text-gray-500 text-sm">
														<p>No payment methods available for this currency.</p>
													</div>
												)}
											</div>
										</>
									)}
								</div>
							</div>

							{/* Order Summary Sidebar */}
							<div className="hidden lg:block lg:col-span-2">
								<div className="bg-gray-50 rounded-lg border border-gray-200 p-4 sticky top-4">
									<h2 className="text-sm font-semibold text-gray-900 mb-3">Summary</h2>

									<div className="space-y-1.5 text-xs">
										<div className="flex justify-between text-gray-600">
											<span>Package</span>
											<span>{subscription?.data?.name}</span>
										</div>
										<div className="flex justify-between text-gray-600">
											<span>Duration</span>
											<span>{duration}</span>
										</div>
										{canAddSeats && (
											<>
												<div className="py-2">
													<label className="block text-gray-600 mb-1">Seats</label>
													<input
														type="number"
														min="1"
														value={numberOfSeats}
														onChange={(e) =>
															setNumberOfSeats(Math.max(1, parseInt(e.target.value) || 1))
														}
														className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-akgreen focus:border-transparent"
														placeholder="Seats"
													/>
												</div>
												<div className="flex justify-between text-gray-600">
													<span>Price per Seat</span>
													<span>
														{getMoneyPrefix(currency)} {baseCost}
													</span>
												</div>
											</>
										)}
									</div>

									{/* Total */}
									<div className="border-t border-gray-200 mt-2.5 pt-2.5">
										<div className="flex justify-between text-sm">
											<span className="font-medium text-gray-900">Total</span>
											<span className="font-semibold text-akgreen">
												{getMoneyPrefix(currency)} {cost}
											</span>
										</div>
									</div>

									{/* Trust Badge */}
									<div className="pt-3 mt-3 border-t border-gray-200">
										<p className="flex items-center justify-center gap-2 text-xs text-gray-500">
											<FaLock className="text-akgreen" size={12} />
											Your payment is secure
										</p>
									</div>
								</div>
							</div>
						</div>
					</div>

					{/* Mobile Sticky Bottom Bar */}
					<div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 lg:hidden z-40 safe-area-inset-bottom">
						<div className="px-4 py-2.5">
							<div className="flex items-center justify-between gap-3">
								<div className="flex items-center gap-3">
									<div>
										<p className="text-xs text-gray-500">1 subscription</p>
										<p className="text-base font-semibold text-gray-900">
											{getMoneyPrefix(currency)}
											{cost}
										</p>
									</div>
									<button
										onClick={() => setShowMobileOrderSummary(true)}
										className="text-akgreen text-xs font-medium"
									>
										Details
									</button>
								</div>
								<span className="text-xs text-gray-400">Select payment above</span>
							</div>
						</div>
					</div>

					{/* Mobile Order Summary Modal */}
					{showMobileOrderSummary && (
						<div className="fixed inset-0 bg-black/40 z-50 lg:hidden">
							<div className="absolute inset-0" onClick={() => setShowMobileOrderSummary(false)} />
							<div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-xl max-h-[70vh] overflow-hidden">
								{/* Header */}
								<div className="flex items-center justify-between px-4 py-2.5 border-b">
									<h2 className="text-sm font-semibold text-gray-900">Order Summary</h2>
									<button
										onClick={() => setShowMobileOrderSummary(false)}
										className="text-gray-400 text-lg leading-none"
									>
										&times;
									</button>
								</div>

								<div className="overflow-y-auto max-h-[calc(70vh-90px)] p-4 space-y-3">
									<div className="flex justify-between text-sm">
										<span className="text-gray-600">Package</span>
										<span className="font-medium text-gray-900">{subscription?.data?.name}</span>
									</div>
									<div className="flex justify-between text-sm">
										<span className="text-gray-600">Duration</span>
										<span className="font-medium text-gray-900">{duration}</span>
									</div>
									{canAddSeats && (
										<>
											<div className="py-2">
												<label className="block text-gray-600 text-sm mb-1">
													Number of Seats/Students
												</label>
												<input
													type="number"
													min="1"
													value={numberOfSeats}
													onChange={(e) =>
														setNumberOfSeats(Math.max(1, parseInt(e.target.value) || 1))
													}
													className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-akgreen focus:border-transparent"
													placeholder="Enter number of seats"
												/>
											</div>
											<div className="flex justify-between text-sm">
												<span className="text-gray-600">Price per Seat</span>
												<span className="font-medium text-gray-900">
													{getMoneyPrefix(currency)} {baseCost}
												</span>
											</div>
										</>
									)}
									<div className="pt-3 border-t space-y-2">
										<div className="flex justify-between text-base font-semibold pt-2 border-t">
											<span>Total</span>
											<span className="text-akgreen">
												{getMoneyPrefix(currency)} {cost}
											</span>
										</div>
									</div>
									<div className="pt-3 border-t">
										<p className="flex items-center justify-center gap-2 text-xs text-gray-500">
											<FaLock className="text-akgreen" size={12} />
											Your payment is secure
										</p>
									</div>
								</div>

								{/* Close Button */}
								<div className="p-3 border-t">
									<button
										onClick={() => setShowMobileOrderSummary(false)}
										className="w-full py-2.5 rounded-md bg-gray-100 text-gray-600 text-sm font-medium"
									>
										Close
									</button>
								</div>
							</div>
						</div>
					)}

					{/* Toast Notification */}
					{/* {showToast && (
						<Toast
							message={toastMessage}
							type="info"
							onClose={() => setShowToast(false)}
							duration={3000}
						/>
					)} */}
				</PaddingX>
			</div>
		</Container>
	);
};

export default SubscriptionCheckout;
